import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import App from './App';
import { useAuthStore } from './store/useAuthStore';
import './styles/global.css';

// Sentry — initialised before React mounts so the existing
// ErrorBoundary below also has its componentDidCatch forwarded by the
// `Sentry.captureException` call inside it. Vite embeds VITE_* env
// vars at build time, so a build without VITE_SENTRY_DSN set just
// no-ops (init is skipped entirely; no network calls).
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Don't mask text — this is an internal annotation tool, the
        // PDF content is the whole reason a replay would be useful.
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // 10% of normal requests get performance traces; Sentry boosts to
    // 100% on captured errors so every error has its request trace.
    tracesSampleRate: 0.1,
    // Session replay: 10% of normal sessions, 100% of sessions with
    // an error. Numbers tuned to stay under the free-tier replay
    // quota; bump in Sentry → Replay if you need more.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });

  // Mirror the auth user into Sentry's scope so every captured event
  // carries user.id / user.username. Hydrate once for the initial
  // state then subscribe for login/logout transitions.
  const applyUser = (u: ReturnType<typeof useAuthStore.getState>['user']): void => {
    if (u) {
      Sentry.setUser({
        id: String(u.id ?? u.username),
        username: u.username,
        // role lets you filter "owner" vs "surveyor" errors in Sentry.
        role: (u as { role?: string }).role,
      });
    } else {
      // Pass null to clear — otherwise a logout's subsequent events
      // would be attributed to the previous user.
      Sentry.setUser(null);
    }
  };
  applyUser(useAuthStore.getState().user);
  useAuthStore.subscribe((s, prev) => {
    if (s.user !== prev.user) applyUser(s.user);
  });
}

// Apply saved theme before first paint to prevent flash
const savedTheme = localStorage.getItem('monco-theme') || 'dark';
document.documentElement.setAttribute('data-theme', savedTheme);

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('React error boundary caught:', error, info);
    // Also send to Sentry with the React component stack as context.
    // The browser SDK's automatic instrumentation catches uncaught
    // promise rejections and window.onerror, but render-time throws
    // are swallowed by the boundary unless we re-emit them here.
    try {
      Sentry.withScope((scope) => {
        scope.setExtra('componentStack', info.componentStack);
        Sentry.captureException(error);
      });
    } catch {
      /* sentry not initialised — nothing to do */
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 20, color: '#f85149', fontFamily: 'monospace', fontSize: 14 }}>
          <h2 style={{ color: '#e6edf3' }}>React Error</h2>
          <pre style={{ whiteSpace: 'pre-wrap', marginTop: 10 }}>
            {this.state.error.message}
            {'\n\n'}
            {this.state.error.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>,
);
